import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../../components/Buttons/Button";
import { ConfirmModal, CreateModal } from "../../components/Modals";
import Sidebar from "../../components/Sidebar";
import { useNotify } from "../../context/NotificationContext";
import { fetchCategories } from "../../services/courseService/courseService";
import {
  createKnowledgeBaseCourse,
  fetchKnowledgeBaseCatalog,
} from "../../services/knowledgeBaseService/knowledgeBaseService";
import type {
  KnowledgeBaseCatalogResponse,
  KnowledgeBaseCourse,
} from "../../services/knowledgeBaseService/knowledgeBaseService.types";
import { getCurrentUserData } from "../../services/settingsService/settingsService";
import { hasRequiredCourseCreationRequisites } from "../../utils/courseCreation";
import { logger } from "../../utils/logger";
import { CoursesPageContainer } from "../CoursesPage/CoursesPage.styles";
import StudentBreadcrumbs from "../StudentPortal/StudentBreadcrumbs";
import { CoursesSectionHeader } from "../StudentPortal/StudentCourseListHeader.styles";
import {
  CatalogCourseCard,
  CourseChevron,
  CourseChip,
  CourseDescription,
  CourseFoot,
  CourseFootStrong,
  CourseFootText,
  CourseList,
  CourseMain,
  CourseMeta,
  CourseSide,
  CourseTitle,
  CourseTop,
  CoursesPageLead,
  CoursesPageShell,
  CoursesPageTitle,
  DemoLoadingState,
  EmptyCourses,
  EmptyCoursesText,
  EmptyCoursesTitle,
  MyCourseCard,
} from "../StudentPortal/studentCourses.styles";
import { KnowledgeBaseCreateAction, KnowledgeBaseStudentMain } from "./KnowledgeBasePage.styles";

const emptyCatalog: KnowledgeBaseCatalogResponse = {
  results: [],
  unread_count: 0,
  has_active_subscription: false,
  can_manage: false,
};

const KnowledgeBasePage: React.FC = () => {
  const [catalog, setCatalog] = useState<KnowledgeBaseCatalogResponse>(emptyCatalog);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCoursePrerequisitesModalOpen, setIsCoursePrerequisitesModalOpen] = useState(false);
  const [isCheckingCoursePrerequisites, setIsCheckingCoursePrerequisites] = useState(false);
  const [isSavingCourse, setIsSavingCourse] = useState(false);
  const toast = useNotify();
  const navigate = useNavigate();

  const loadCatalog = useCallback(async () => {
    try {
      const data = await fetchKnowledgeBaseCatalog();
      setCatalog(data);
    } catch (error) {
      logger.error("Ошибка загрузки Базы знаний:", error);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить Базу знаний",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!catalog.can_manage) return;

    fetchCategories()
      .then(setCategories)
      .catch((error) => logger.error("Ошибка загрузки категорий Базы знаний:", error));
  }, [catalog.can_manage]);

  const openCreateModal = async () => {
    if (isCheckingCoursePrerequisites) return;

    setIsCheckingCoursePrerequisites(true);
    try {
      const user = await getCurrentUserData();
      if (!hasRequiredCourseCreationRequisites(user)) {
        setIsCoursePrerequisitesModalOpen(true);
        return;
      }
      setIsCreateModalOpen(true);
    } catch (error) {
      logger.error("Ошибка проверки реквизитов перед созданием курса Базы знаний:", error);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось проверить реквизиты преподавателя. Попробуйте ещё раз.",
        life: 4000,
      });
    } finally {
      setIsCheckingCoursePrerequisites(false);
    }
  };

  const handleCreateCourse = async (
    formData: Record<string, string | File[] | null>,
  ) => {
    setIsSavingCourse(true);
    try {
      await createKnowledgeBaseCourse({
        title: String(formData.courseName || "").trim(),
        category: String(formData.category || "").trim(),
        description: String(formData.description || "").trim(),
      });
      setIsCreateModalOpen(false);
      await loadCatalog();
      toast.show({
        severity: "success",
        summary: "Готово",
        detail: "Курс Базы знаний создан",
        life: 2500,
      });
    } catch (error) {
      logger.error("Ошибка создания курса Базы знаний:", error);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось создать курс Базы знаний",
        life: 3500,
      });
    } finally {
      setIsSavingCourse(false);
    }
  };

  const renderStatus = (course: KnowledgeBaseCourse) => {
    if (course.is_locked) return <CourseChip>Доступно на PRO и EXPERT</CourseChip>;
    if (!course.is_approved_by_admin) return <CourseChip>Черновик</CourseChip>;
    if (course.is_new) return <CourseChip $done>Новое</CourseChip>;
    return null;
  };

  const renderCourseContent = (course: KnowledgeBaseCourse, showChevron: boolean) => {
    const status = renderStatus(course);

    return (
      <>
        <CourseTop>
          <CourseMain>
            <CourseMeta>
              {course.instructor.organization_name} · {course.category}
            </CourseMeta>
            <CourseTitle>{course.title}</CourseTitle>
            {course.description ? (
              <CourseDescription>{course.description}</CourseDescription>
            ) : null}
          </CourseMain>
          {status ? <CourseSide>{status}</CourseSide> : null}
        </CourseTop>

        <CourseFoot>
          <CourseFootText>
            Материалов в курсе: <CourseFootStrong>{course.lesson_count}</CourseFootStrong>
          </CourseFootText>
          {showChevron ? <CourseChevron aria-hidden="true" /> : null}
        </CourseFoot>
      </>
    );
  };

  return (
    <CoursesPageContainer>
      <Sidebar />
      <KnowledgeBaseStudentMain>
        {catalog.can_manage ? (
          <KnowledgeBaseCreateAction>
            <Button
              onClick={() => void openCreateModal()}
              type="button"
              variant="primary"
              size="md"
              disabled={isCheckingCoursePrerequisites}
            >
              Новый курс
            </Button>
          </KnowledgeBaseCreateAction>
        ) : null}

        <CoursesPageShell>
          <CoursesSectionHeader>
            <StudentBreadcrumbs items={[{ label: "База знаний" }]} />
            <CoursesPageTitle>База знаний</CoursesPageTitle>
            <CoursesPageLead>
              Курсы и материалы, доступные в Базе знаний.
            </CoursesPageLead>
          </CoursesSectionHeader>

          {loading ? (
            <DemoLoadingState>Загружаем курсы…</DemoLoadingState>
          ) : catalog.results.length === 0 ? (
            <EmptyCourses>
              <EmptyCoursesTitle>В Базе знаний пока нет курсов</EmptyCoursesTitle>
              <EmptyCoursesText>
                Опубликованные курсы Базы знаний появятся здесь.
              </EmptyCoursesText>
            </EmptyCourses>
          ) : (
            <CourseList>
              {catalog.results.map((course) =>
                course.is_locked ? (
                  <CatalogCourseCard key={course.id}>
                    {renderCourseContent(course, false)}
                  </CatalogCourseCard>
                ) : (
                  <MyCourseCard key={course.id} to={`/knowledge-base/${course.id}`}>
                    {renderCourseContent(course, true)}
                  </MyCourseCard>
                ),
              )}
            </CourseList>
          )}
        </CoursesPageShell>

        <ConfirmModal
          isOpen={isCoursePrerequisitesModalOpen}
          title="Заполните реквизиты"
          message="Перед созданием нового курса заполните в настройках ИНН, ОГРН/ОГРНИП и данные электронной подписи."
          confirmButtonText="Перейти в настройки"
          onConfirm={() => {
            setIsCoursePrerequisitesModalOpen(false);
            navigate("/settings");
          }}
          onCancel={() => setIsCoursePrerequisitesModalOpen(false)}
        />

        {isCreateModalOpen ? (
          <CreateModal
            title="Новый курс"
            fields={[
              { name: "courseName", type: "text", placeholder: "Название курса", required: true },
              {
                name: "category",
                type: "selectWithInput",
                placeholder: "Категория",
                options: categories,
                required: true,
              },
              { name: "description", type: "textarea", placeholder: "Описание" },
            ]}
            onSubmit={handleCreateCourse}
            submitButtonText="Создать"
            onCancel={() => setIsCreateModalOpen(false)}
            hasDragAndDrop={false}
            isLoading={isSavingCourse}
          />
        ) : null}
      </KnowledgeBaseStudentMain>
    </CoursesPageContainer>
  );
};

export default KnowledgeBasePage;
